/**
 * Persistent right panel — mount + kill-switch + open-state helpers
 * (#194 IA-rework PR 1; #1920 closed-by-default overlay).
 *
 * The panel mounts on lead-side `/teams/:tid*` + `/cycles/:cid*` and on
 * the member-side `/me*` route family; everywhere else nothing mounts.
 *
 * **The model since #1920** (R1 + R2 of
 * `docs/agent-prompts/handover-right-panel-and-inbox-2026-10-02.md`).
 * The founder found the always-open column jarring and the grid it sat in
 * shrank the page. Now the page is always one full-width column; the panel
 * is an overlay drawer (`ui/SideDrawer`) that slides in over the page's
 * right edge and costs it no width. It is **closed by default**: a fresh
 * browser sees the full page and a handle (with a count badge) at the top
 * right. Opening it is remembered per browser; the handle, `Esc` and a
 * move to a different team or cycle close it.
 *
 * **Two independent localStorage flags.**
 *
 * - **Kill-switch** (`tarii.persistent_panel_disabled`) — "I never want
 *   this panel." Removes the entire mount, handle and drawer both. The
 *   IA-2 first-mount restoration precedent; also the incident lever.
 *
 * - **Open flag** (`tarii.persistent_panel_open`) — `"1"` = open, absent =
 *   closed (the default). Written when the user opens the drawer, removed
 *   when they close it.
 *
 * The two flags are intentionally separate so that flipping one doesn't
 * toggle the other — a user who killed the panel via dev-tools must not
 * have their kill state cleared the next time they open the drawer.
 *
 * **Migration.** Until #1920 the flag was the inverse — a collapse flag,
 * `tarii.persistent_panel_collapsed`, absent = expanded. It is ignored
 * now (everyone starts closed) and removed the first time the open flag
 * is read.
 *
 * Centralising the path-matching + flag handling here means the panel
 * sections + the AppShell layout don't re-implement the regex / key shape
 * at each call site.
 */

import { useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";

const KILL_SWITCH_KEY = "tarii.persistent_panel_disabled";
const KILL_SWITCH_VALUE = "1";

const OPEN_KEY = "tarii.persistent_panel_open";
const OPEN_VALUE = "1";

/** The pre-#1920 collapse flag — ignored, and removed on first read. */
const LEGACY_COLLAPSED_KEY = "tarii.persistent_panel_collapsed";

/** localStorage kill-switch key — exported for tests + AppShell. */
export const PERSISTENT_PANEL_KILL_SWITCH_KEY = KILL_SWITCH_KEY;

/** localStorage open-state key — exported for tests + AppShell.
 * Distinct from the kill-switch key by design (see module docstring). */
export const PERSISTENT_PANEL_OPEN_KEY = OPEN_KEY;

/** The retired collapse key — exported so tests can pin its migration. */
export const PERSISTENT_PANEL_LEGACY_COLLAPSED_KEY = LEGACY_COLLAPSED_KEY;

export type PersistentPanelVariant = "lead" | "member" | null;

/**
 * Resolve the panel variant for the current pathname. Returns:
 *   - `"lead"` for `/teams/:tid*` and `/cycles/:cid*` paths
 *   - `"member"` for `/me*` paths
 *   - `null` for any other path (no panel)
 *
 * `:tid` / `:cid` always resolve to a UUID-shaped segment in this app
 * but the variant decision is purely structural (path prefix), so a
 * loose match on the leading segment is sufficient. Keeps the helper
 * cheap on every navigation tick.
 */
export function resolvePanelVariant(pathname: string): PersistentPanelVariant {
  if (pathname === "/teams" || pathname === "/cycles") {
    // The list pages (no `:tid`/`:cid` selected) don't get a panel — there's
    // no team / cycle context yet. Single-column.
    return null;
  }
  if (pathname.startsWith("/teams/") || pathname.startsWith("/cycles/")) {
    return "lead";
  }
  if (pathname === "/me" || pathname.startsWith("/me/")) {
    return "member";
  }
  return null;
}

/**
 * The context a panel belongs to: `teams/<id>` or `cycles/<id>` for the
 * lead, `me` for the member, `null` elsewhere. The layout closes the
 * drawer when this changes between two non-null values — a move to a
 * different team or cycle — and keeps it open across the pages of one
 * (`/cycles/<id>/roster` → `/cycles/<id>/hours`).
 */
export function persistentPanelContextRoot(pathname: string): string | null {
  const match = pathname.match(/^\/(teams|cycles)\/([^/]+)/);
  if (match) return `${match[1]}/${match[2]}`;
  if (pathname === "/me" || pathname.startsWith("/me/")) return "me";
  return null;
}

/** Read the kill-switch from localStorage. SSR-safe — returns `false`
 * when `window` is unavailable. */
export function isPersistentPanelDisabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(KILL_SWITCH_KEY) === KILL_SWITCH_VALUE;
  } catch {
    // localStorage can throw in private-browsing modes / strict CSP.
    // Default to enabled so the panel still renders for those users;
    // they keep the AppShell behaviour they had before #194.
    return false;
  }
}

/**
 * Resolve the variant for the current location. The hook combines
 * `resolvePanelVariant` (route-based decision) with the kill-switch check;
 * AppShell uses it to decide whether to mount the handle + drawer.
 *
 * Returns `null` when no panel should mount (kill-switch on, or path
 * doesn't match any variant). Otherwise returns the variant.
 */
export function usePersistentPanelVariant(): PersistentPanelVariant {
  const { pathname } = useLocation();
  const disabled = isPersistentPanelDisabled();
  if (disabled) return null;
  return resolvePanelVariant(pathname);
}

/** Read the open flag from localStorage. SSR-safe — returns `false`
 * (closed) when `window` is unavailable or storage throws. Drops the
 * retired collapse key if it is still there (see module docstring). */
export function isPersistentPanelOpen(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const storage = window.localStorage;
    if (storage.getItem(LEGACY_COLLAPSED_KEY) !== null) {
      storage.removeItem(LEGACY_COLLAPSED_KEY);
    }
    return storage.getItem(OPEN_KEY) === OPEN_VALUE;
  } catch {
    // localStorage can throw in private-browsing modes / strict CSP.
    // Closed is the default for everyone, so it is the safe fallback.
    return false;
  }
}

/**
 * Open or close the panel — a plain function, not a hook, so code outside
 * the layout can call it (R6: opening a roster surface closes the
 * next-steps drawer — one drawer slot). Notifies every
 * `usePersistentPanelOpen` consumer in this tab; other tabs follow via the
 * native `storage` event.
 */
export function setPersistentPanelOpen(next: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (next) {
      window.localStorage.setItem(OPEN_KEY, OPEN_VALUE);
    } else {
      window.localStorage.removeItem(OPEN_KEY);
    }
    // The `storage` event doesn't fire in the tab that wrote — dispatch a
    // synthetic one so same-tab subscribers re-read too.
    window.dispatchEvent(new StorageEvent("storage", { key: OPEN_KEY }));
  } catch {
    // Best-effort — same fallback as isPersistentPanelOpen.
  }
}

/**
 * Reactive open-state hook with cross-tab sync. Returns `[open, setOpen]`.
 * `useSyncExternalStore` over the localStorage key, subscribed to the
 * `storage` event (native cross-tab + the synthetic same-tab dispatch in
 * `setPersistentPanelOpen`).
 *
 * Distinct from `isPersistentPanelDisabled()` per module docstring.
 */
export function usePersistentPanelOpen(): [boolean, (next: boolean) => void] {
  const open = useSyncExternalStore(subscribeOpenKey, isPersistentPanelOpen, () => false);
  return [open, setPersistentPanelOpen];
}

function subscribeOpenKey(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const handler = (event: StorageEvent) => {
    if (event.key === null || event.key === OPEN_KEY) onChange();
  };
  window.addEventListener("storage", handler);
  return () => window.removeEventListener("storage", handler);
}
